import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, Container, FormGroup, Input, Label, Modal, Row } from 'reactstrap';
import { ComboBox } from '../../../dropdowns';
import PropTypes from "prop-types";
import { FileDropzone, MultipleDropzoneList } from '../../../FileDropzone';
import fileDownload from 'js-file-download';
import api from "../../../../services/api";
import Loader from '../../../loaders';

export default function JobOrderCandidateMilestoneDocumentDialog(props) {
    const {jobOrderCandidates, milestoneName, canViewDocumentTemplate, onClose, onSubmit} = props;
    const [loading, setLoading] = useState(false);
    const [itemInEdit, setItemInEdit] = useState({jobOrderCandidates, milestoneName, canViewDocumentTemplate, useTemplate: true, files: []});
    const [selectedTemplate, setSelectedTemplate] = useState(null);

    const positionId = useMemo(() => {
        const positionIds = [...new Set(jobOrderCandidates?.map(joc => joc.positionId) ?? [])];
        return positionIds.length === 1 ? positionIds[0] : null;
    }, [jobOrderCandidates]);

    const downloadAttachment = (fileInfo = null, downloadUrl = null) => {
        if (fileInfo && fileInfo instanceof File) {
            fileDownload(fileInfo, fileInfo.name);
            return;
        }

        if (!downloadUrl)
            return;

        setLoading(true);
        let url = downloadUrl;
        api.get(url, { responseType: 'blob' })
        .then(blob => {
            fileDownload(blob.data, fileInfo.filename);
        }).catch(error => {
            console.error(error)
        }).finally(() => setLoading(false))
    }

    const removeAttachment = useCallback((fileInfo) => {
        const newAttachments = [...itemInEdit.files];
        const removedItem = newAttachments.filter(a => a.jobOrderCandidateMilestoneDocumentId === fileInfo.jobOrderCandidateMilestoneDocumentId)[0];
        const removedIndex = newAttachments.indexOf(removedItem);
        newAttachments.splice(removedIndex, 1);
        setItemInEdit({ ...itemInEdit, files: [...newAttachments] });
    }, [itemInEdit]);

    const files = useMemo(() => {
        if (!itemInEdit.files.length)
            return null;

        return <MultipleDropzoneList
            idField="jobOrderCandidateMilestoneDocumentId"
            nameField="filename"
            sizeField="fileSizeInKBytes"
            download={downloadAttachment}
            remove={removeAttachment}
            data={itemInEdit.files}
        />;
    }, [ itemInEdit.files ]);

    const validateDocument = useCallback(() => {
        if (!itemInEdit.files.length)
            return;

        onSubmit(itemInEdit);
    }, [itemInEdit]);

    return (
        <Modal
            isOpen={true}
        >
        {
            loading && <Loader />
        }
            <div className="modal-header">
                <h5 className="modal-title">Milestone Document - {itemInEdit?.jobOrderCandidates?.map(joc => joc.candidateName).join(", ")}</h5>
                <button
                    aria-label="Close"
                    className="close"
                    onClick={onClose}
                    type="button"
                >
                    <span aria-hidden={true}><i className="fas fa-times-circle" /></span>
                </button>
            </div>
            <div className="modal-body">
                <Container fluid className="px-0">
                    <Row>
                        <Col md={12} className="border-right border-black">
                            <FormGroup className="mb-3 pb-3 border-bottom-3">
                                <h6 className="modal-title">{itemInEdit?.milestoneName ?? ""}</h6>
                            </FormGroup>
                        </Col>
                    {
                        positionId && canViewDocumentTemplate ?
                        <Col md={12} className="border-right border-black">
                            <FormGroup className="mb-3 pb-3 border-bottom-3">
                                <Label>
                                    Select Interview Template to download
                                </Label>
                                <ComboBox
                                    minLength={0}
                                    isLookup={false}
                                    idField="milestoneDocumentId"
                                    valueField="templateName"
                                    endpoint={`/milestonedocument/${positionId}/position`}
                                    selectedItem={selectedTemplate}
                                    onChange={(item) => {
                                        setSelectedTemplate(item);
                                    }}
                                />
                            </FormGroup>
                        </Col>
                        :
                        null
                    }
                    {
                        selectedTemplate ?
                        <Col xs={12} className="m-0 p-0">
                            <ul style={{ listStyleType: 'none' }}>
                                <li className="pt-1">
                                    <a className="cursor-pointer font-size-small-custom" onClick={() => downloadAttachment(selectedTemplate, `milestonedocument/${selectedTemplate.milestoneDocumentId}/download`)}><i className="fas fa-download mr-2" />{selectedTemplate.templateName} ({selectedTemplate.filename})</a>
                                </li> 
                            </ul>
                        </Col>
                        :
                        null
                    }
                        <FileDropzone
                            files={files}
                            onDrop={([file]) => {
                                const newAttachments = [...itemInEdit.files];
                                const newIds = newAttachments?.filter(a => a.jobOrderCandidateMilestoneDocumentId <= 0);
                                if (newIds.length)
                                    newIds.sort((a, b) => parseInt(b.jobOrderCandidateMilestoneDocumentId) - parseInt(a.jobOrderCandidateMilestoneDocumentId));

                                const newId = newIds.length ? newIds[0].jobOrderCandidateMilestoneDocumentId - 1 : 0;
                                const newData = {
                                    jobOrderCandidateMilestoneDocumentId: newId,
                                    file
                                };
                                newAttachments.push(newData);

                                setItemInEdit({ ...itemInEdit, files: [...newAttachments] });
                            }}
                            maxFiles={0}
                        />
                    </Row>
                </Container>
            </div>
            <div
                className="modal-footer"
            >
                <Button
                    color="secondary"
                    onClick={onClose}
                    type="button"
                >
                    Close
                </Button>
                <Button
                    color="default"
                    onClick={() => validateDocument()}
                    type="button"
                    disabled={!itemInEdit.files.length}
                >
                    Submit
                </Button>
            </div>
        </Modal>
    );
}

JobOrderCandidateMilestoneDocumentDialog.propTypes = {
    jobOrderCandidates: PropTypes.array.isRequired,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
}